import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  CheckIcon, ChevronDownIcon, ChevronUpIcon, ExternalLinkIcon, HeartPulseIcon, MailIcon, MarsIcon, MinusIcon, PhoneIcon,
  ShieldAlertIcon, VenusIcon, XIcon,
} from 'lucide-react';
import { formatCurrency, formatDate, formatDay, formatDayRange, fullName } from '../../utils/format';
import { PAYMENT_STATE_LABELS, STATUS_NOTE_MAX, balanceDue, documentChecklist, medicalReviewLabel } from '../../utils/registrations';
import { CopyChip } from '../table/CopyChip';
import { StatusBadge } from '../ui/StatusBadge';

/*
 * Read-only details for one registration (docs/director-reporting-prototype.md, P7).
 *
 * Changes from the reference: the status select is a badge plus "Open full booking" (status,
 * cancel / refund, documents and payments stay on the booking page); no emergency contact (not
 * collected); documents are the four booking-page rules plus medical review, with no "Mark
 * received"; payment shows the booking's figures, shared by every participant on the booking;
 * Q&A is the row's own answers; notes stay on the booking page.
 *
 * "Open full booking" opens the booking page (/bookings/:bookingId/:camperId/:programId) in a new
 * tab, so the report and its filters stay as they are; it takes a while to load on a big camp.
 *
 * Portalled to <body>: the page sits inside .body_container (z-index 1), under the fixed site
 * header (z-index 500), so the drawer needs its own stacking level above both.
 */

const isNone = value => /^none$/i.test(String(value || '').trim());

export function RegistrationDrawer({ registration: r, onClose, onPrev, onNext, onSaveStatusNote, position }) {
  const closeRef = useRef(null);

  useEffect(() => {
    if (!r) return undefined;
    const onKey = e => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [r, onClose]);

  // Focus the drawer when it opens, so J / K and Escape work without a click.
  const open = Boolean(r);
  useEffect(() => {
    if (open) closeRef.current?.focus({ preventScroll: true });
  }, [open]);

  const navButton =
    'tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-text-neutral-500 tw-transition-colors hover:tw-bg-sand-50 hover:tw-text-neutral-900 disabled:tw-cursor-not-allowed disabled:tw-opacity-30 disabled:hover:tw-bg-transparent';

  const p = r?.participant;
  const gender = String(p?.gender || '').toLowerCase();
  const GenderIcon = gender.startsWith('m') ? MarsIcon : gender.startsWith('f') ? VenusIcon : null;
  const facts = r
    ? [p.age !== null && p.age !== '' ? `Age ${p.age}` : '', p.grade ? `Grade ${p.grade}` : '', r.bookingNumber ? `Booking ${r.bookingNumber}` : '']
        .filter(Boolean)
        .join(' · ')
    : '';

  return createPortal(
    <AnimatePresence>
      {r && (
        <div className="reports-page tw-font-sans">
          <motion.div
            key="scrim"
            className="tw-fixed tw-inset-0 tw-z-[590] tw-bg-neutral-900/20"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            aria-hidden
          />
          <motion.aside
            key="drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="reports-drawer-title"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.28, ease: [0.23, 1, 0.32, 1] }}
            className="tw-fixed tw-inset-y-0 tw-right-0 tw-z-[600] tw-flex tw-w-full tw-max-w-[480px] tw-flex-col tw-border-l tw-border-neutral-200 tw-bg-white tw-text-neutral-900 tw-shadow-xl">
            <div className="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-border-b tw-border-neutral-200 tw-px-4 tw-py-5 sm:tw-px-6">
              <div className="tw-min-w-0">
                <h2 id="reports-drawer-title" className="tw-m-0 tw-flex tw-items-center tw-gap-2 tw-text-lg tw-font-semibold tw-text-neutral-900">
                  <span className="tw-truncate">{fullName(r) || 'Unnamed participant'}</span>
                  {GenderIcon && (
                    <GenderIcon className={`tw-h-4 tw-w-4 tw-shrink-0 ${GenderIcon === MarsIcon ? 'tw-text-blue-700' : 'tw-text-red-700/70'}`} aria-label={p.gender} />
                  )}
                </h2>
                {facts && <p className="tw-mt-0.5 tw-text-sm tw-text-neutral-500">{facts}</p>}
              </div>
              <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-1">
                {position.index >= 0 && (
                  <span className="tw-mr-1 tw-text-xs tw-tabular-nums tw-text-neutral-400">
                    {position.index + 1} of {position.total}
                  </span>
                )}
                <button type="button" onClick={onPrev} disabled={position.index <= 0} aria-label="Previous participant (K)" title="Previous (K)" className={navButton}>
                  <ChevronUpIcon className="tw-h-4 tw-w-4" />
                </button>
                <button
                  type="button"
                  onClick={onNext}
                  disabled={position.index < 0 || position.index >= position.total - 1}
                  aria-label="Next participant (J)"
                  title="Next (J)"
                  className={navButton}>
                  <ChevronDownIcon className="tw-h-4 tw-w-4" />
                </button>
                <span className="tw-mx-1 tw-h-5 tw-w-px tw-bg-neutral-200" aria-hidden />
                <button ref={closeRef} type="button" onClick={onClose} aria-label="Close details" className={navButton}>
                  <XIcon className="tw-h-5 tw-w-5" />
                </button>
              </div>
            </div>

            <div className="scrollbar-thin tw-flex-1 tw-space-y-7 tw-overflow-y-auto tw-px-4 tw-py-6 sm:tw-px-6">
              <section className="tw-grid tw-grid-cols-2 tw-gap-4 sm:tw-grid-cols-3">
                <div>
                  <p className="tw-text-xs tw-text-neutral-500">Status</p>
                  <div className="tw-mt-1.5">
                    <StatusBadge status={r.status} title={r.status === 'Pending' ? 'Payment pending' : undefined} />
                  </div>
                </div>
                <div>
                  <p className="tw-text-xs tw-text-neutral-500">Program</p>
                  <p className="tw-mt-1 tw-text-sm tw-font-medium tw-text-neutral-900">
                    {r.program.programName || '—'}
                    {r.program.isDeleted && <span className="tw-ml-1 tw-text-xs tw-font-normal tw-text-neutral-400">(deleted)</span>}
                  </p>
                  <p className="tw-text-xs tw-text-neutral-400">{formatDayRange(r.program.startDate, r.program.endDate)}</p>
                </div>
                <div>
                  <p className="tw-text-xs tw-text-neutral-500">Stay</p>
                  <p className="tw-mt-1 tw-text-sm tw-font-medium tw-text-neutral-900">{r.stay || '—'}</p>
                </div>
              </section>

              <section className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-rounded-xl tw-bg-sand-50 tw-px-4 tw-py-3">
                <p className="tw-text-sm tw-text-neutral-500">Status, cancellation, refunds, documents and payments are managed on the booking.</p>
                <a
                  href={`/bookings/${r.bookingId}/${r.camperId}/${r.programId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw-inline-flex tw-h-9 tw-shrink-0 tw-items-center tw-gap-1.5 tw-rounded-lg tw-bg-brand-600 tw-px-3.5 tw-text-sm tw-font-medium tw-text-white tw-no-underline tw-transition-colors hover:tw-bg-brand-700 hover:tw-text-white hover:tw-no-underline">
                  Open full booking
                  <ExternalLinkIcon className="tw-h-3.5 tw-w-3.5" aria-hidden />
                </a>
              </section>

              <StatusNoteField key={r.id} r={r} onSave={onSaveStatusNote} />

              <section aria-labelledby="reports-drawer-participant">
                <h3 id="reports-drawer-participant" className="tw-m-0 tw-text-sm tw-font-semibold tw-text-neutral-900">
                  Participant
                </h3>
                <dl className="tw-m-0 tw-mt-3 tw-grid tw-grid-cols-2 tw-gap-4 sm:tw-grid-cols-3">
                  <Field label="Date of birth">{formatDay(p.dob)}</Field>
                  <Field label="T-shirt size">{p.tshirtSize}</Field>
                  <Field label="Shorts size">{p.shortsSize}</Field>
                  {(p.groupCode || p.group) && <Field label="Group code">{p.groupCode || p.group}</Field>}
                  <div className="tw-col-span-2 sm:tw-col-span-3">
                    <dt className="tw-text-xs tw-font-normal tw-text-neutral-500">Address</dt>
                    <dd className="tw-m-0 tw-mt-0.5 tw-text-sm tw-text-neutral-900">
                      {r.address.street || r.address.city ? (
                        <>
                          {r.address.street}
                          {r.address.apt && `, ${r.address.apt}`}
                          <br />
                          {[r.address.city, [r.address.state, r.address.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
                        </>
                      ) : (
                        <span className="tw-text-neutral-400">Not provided</span>
                      )}
                    </dd>
                  </div>
                </dl>
              </section>

              <section aria-labelledby="reports-drawer-guardians">
                <h3 id="reports-drawer-guardians" className="tw-m-0 tw-text-sm tw-font-semibold tw-text-neutral-900">
                  Guardians
                </h3>
                <div className="tw-mt-3 tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
                  <ContactBlock label="Primary" contact={r.primaryGuardian} />
                  <ContactBlock label="Secondary" contact={r.secondaryGuardian} />
                </div>
              </section>

              <PaymentSection r={r} />

              <section className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
                <div>
                  <h3 className="tw-m-0 tw-text-sm tw-font-semibold tw-text-neutral-900">Additional experience</h3>
                  {r.extras.length ? (
                    <ul className="tw-mt-1.5 tw-space-y-0.5 tw-text-sm tw-text-neutral-900">
                      {r.extras.map((e, i) => (
                        <li key={`${e.title}-${i}`} className="tw-flex tw-justify-between tw-gap-2">
                          <span className="tw-truncate">{e.title}</span>
                          {Number(e.price) > 0 && <span className="tw-shrink-0 tw-tabular-nums tw-text-neutral-500">{formatCurrency(e.price)}</span>}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="tw-mt-1.5 tw-text-sm tw-text-neutral-400">None added</p>
                  )}
                </div>
                <div>
                  <h3 className="tw-m-0 tw-text-sm tw-font-semibold tw-text-neutral-900">Insurance company</h3>
                  <p className={`tw-mt-1.5 tw-text-sm ${p.insuranceCompany ? 'tw-text-neutral-900' : 'tw-font-medium tw-text-amber-700'}`}>{p.insuranceCompany || 'Not on file'}</p>
                </div>
              </section>

              <MedicalSection r={r} />

              <DocumentsSection r={r} />

              <section aria-labelledby="reports-drawer-questions">
                <h3 id="reports-drawer-questions" className="tw-m-0 tw-text-sm tw-font-semibold tw-text-neutral-900">
                  Registration questions
                </h3>
                {r.answers.length ? (
                  <dl className="tw-m-0 tw-mt-2 tw-divide-y tw-divide-neutral-200">
                    {r.answers.map((a, i) => (
                      <div key={`${a.questionId}-${i}`} className="tw-py-2.5">
                        <dt className="tw-text-xs tw-font-normal tw-text-neutral-500">
                          {i + 1}. {a.question || 'Untitled question'}
                        </dt>
                        <dd className={`tw-m-0 tw-mt-0.5 tw-text-sm ${a.answer ? 'tw-text-neutral-900' : 'tw-text-neutral-400'}`}>{a.answer || 'No answer'}</dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p className="tw-mt-1.5 tw-text-sm tw-text-neutral-400">No questions answered</p>
                )}
              </section>

              <section aria-labelledby="reports-drawer-notes">
                <h3 id="reports-drawer-notes" className="tw-m-0 tw-text-sm tw-font-semibold tw-text-neutral-900">
                  Notes & activity
                </h3>
                <p className="tw-mt-1.5 tw-text-sm tw-text-neutral-500">Notes and activity for this registration are on the full booking.</p>
              </section>
            </div>
          </motion.aside>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}

/*
 * The row's status note, editable (the table's StatusNoteCell does the same in a cell). Leaving the
 * box or Enter saves; Escape puts the saved note back without closing the drawer. Keyed by row, so
 * J / K start from the next row's note. `onSave(row, text)` resolves true once the API has saved it;
 * the box shows "Saving…" until then, and keeps the typed text if it fails (the page shows the error).
 */
function StatusNoteField({ r, onSave }) {
  const [draft, setDraft] = useState(r.statusNote);
  const [saving, setSaving] = useState(false);
  // Also set while saving: disabling the focused box can fire blur, which must not save twice.
  const savingRef = useRef(false);
  const mounted = useRef(true);

  // Set on every mount: StrictMode (dev) mounts, unmounts and mounts again.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // The note saved elsewhere (the table cell) or by this box: show it.
  useEffect(() => {
    if (!savingRef.current) setDraft(r.statusNote);
  }, [r.statusNote]);

  const save = async () => {
    if (savingRef.current) return;
    const next = draft.trim();
    if (next === r.statusNote) {
      setDraft(next);
      return;
    }
    savingRef.current = true;
    setSaving(true);
    const ok = await onSave(r, next);
    savingRef.current = false;
    if (!mounted.current) return;
    setSaving(false);
    if (ok) setDraft(next);
  };

  return (
    <section>
      <label htmlFor="reports-drawer-status-note" className="tw-flex tw-justify-between tw-text-xs tw-text-neutral-500">
        Status note
        <span className="tw-tabular-nums tw-text-neutral-400">{saving ? 'Saving…' : `${draft.length}/${STATUS_NOTE_MAX}`}</span>
      </label>
      <input
        id="reports-drawer-status-note"
        value={draft}
        maxLength={STATUS_NOTE_MAX}
        disabled={saving}
        onChange={e => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={e => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') {
            // Keep the drawer open: the drawer closes on Escape from the document.
            e.stopPropagation();
            setDraft(r.statusNote);
          }
        }}
        placeholder="Add a short status note"
        className="tw-mt-1 tw-h-9 tw-w-full tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-white tw-px-3 tw-text-sm tw-text-neutral-900 tw-outline-none tw-transition-[border-color,box-shadow] tw-duration-150 placeholder:tw-text-neutral-400 focus:tw-border-brand-500 focus:tw-ring-2 focus:tw-ring-brand-500/20 disabled:tw-bg-sand-50 disabled:tw-text-neutral-500"
      />
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div className="tw-min-w-0">
      <dt className="tw-text-xs tw-font-normal tw-text-neutral-500">{label}</dt>
      <dd className={`tw-m-0 tw-mt-0.5 tw-truncate tw-text-sm ${children ? 'tw-text-neutral-900' : 'tw-text-neutral-400'}`}>{children || '—'}</dd>
    </div>
  );
}

function ContactBlock({ label, contact }) {
  return (
    <div className="tw-min-w-0">
      <p className="tw-text-xs tw-text-neutral-500">{label}</p>
      {contact && contact.name ? (
        <>
          <p className="tw-mt-0.5 tw-text-sm tw-font-medium tw-text-neutral-900">{contact.name}</p>
          <div className="-tw-ml-1.5 tw-mt-1 tw-flex tw-flex-col tw-items-start">
            <CopyChip icon={MailIcon} value={contact.email} label="email" />
            <CopyChip icon={PhoneIcon} value={contact.phone} label="phone" />
          </div>
        </>
      ) : (
        <p className="tw-mt-0.5 tw-text-sm tw-text-neutral-400">Not provided</p>
      )}
    </div>
  );
}

// Booking figures (money.booking), shared by every participant on the booking, and
// this participant's own lines (money.participantFees), labelled as on the booking page.
function PaymentSection({ r }) {
  const b = r.money?.booking || {};
  const f = r.money?.participantFees || {};
  const shared = (b.participantsOnBooking || 1) > 1;
  const due = balanceDue(r);
  const stateTone =
    b.paymentState === 'overdue'
      ? 'tw-bg-red-50 tw-text-red-700'
      : b.paymentState === 'balance_due'
        ? 'tw-bg-amber-50 tw-text-amber-700'
        : b.paymentState === 'pending'
          ? 'tw-bg-blue-50 tw-text-blue-700'
          : b.paymentState === 'paid'
            ? 'tw-bg-green-50 tw-text-green-700'
            : 'tw-bg-neutral-100 tw-text-neutral-500';
  const lines = [
    [f.feeLabel || 'Program Fee', f.programFee, 1],
    ['Program Discount', f.programDiscount, -1],
    ['Discount Applied', f.couponDiscount, -1],
    ['Multi Program Discount', f.multiProgramDiscount, -1],
    ['Sibling Discount', f.siblingDiscount, -1],
    ['Convenience Fee', f.convenienceFee, 1],
    ['Non-Refundable Fee', f.nonRefundableFee, 1],
    ['Additional experience', f.extrasTotal, 1],
    ['Additional charges', f.additionalCharges, 1],
    ['Insurance', f.insurance, 1],
  ].filter(([, value]) => Number(value) > 0);

  return (
    <section aria-labelledby="reports-drawer-payment">
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
        <h3 id="reports-drawer-payment" className="tw-m-0 tw-text-sm tw-font-semibold tw-text-neutral-900">
          Payment
        </h3>
        {b.paymentState && (
          <span className={`tw-rounded-full tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium ${stateTone}`}>{PAYMENT_STATE_LABELS[b.paymentState] || b.paymentState}</span>
        )}
      </div>
      <dl className="tw-m-0 tw-mt-2 tw-grid tw-grid-cols-3 tw-gap-4 tw-rounded-xl tw-bg-sand-50 tw-p-4">
        <div>
          <dt className="tw-text-xs tw-font-normal tw-text-neutral-500">Booking total</dt>
          <dd className="tw-m-0 tw-mt-0.5 tw-text-sm tw-font-medium tw-tabular-nums tw-text-neutral-900">{formatCurrency(b.total)}</dd>
        </div>
        <div>
          <dt className="tw-text-xs tw-font-normal tw-text-neutral-500">Paid</dt>
          <dd className="tw-m-0 tw-mt-0.5 tw-text-sm tw-font-medium tw-tabular-nums tw-text-green-700">{formatCurrency(b.paid)}</dd>
        </div>
        <div>
          <dt className="tw-text-xs tw-font-normal tw-text-neutral-500">Balance due</dt>
          <dd className={`tw-m-0 tw-mt-0.5 tw-text-sm tw-font-medium tw-tabular-nums ${due ? 'tw-text-amber-700' : 'tw-text-neutral-900'}`}>{formatCurrency(due)}</dd>
        </div>
        <div className="tw-col-span-3 tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-1 tw-border-t tw-border-neutral-200 tw-pt-3 tw-text-xs tw-text-neutral-500">
          {shared && <span className="tw-font-medium tw-text-neutral-700">Shared with {b.participantsOnBooking} participants on this booking</span>}
          {b.paymentMethod && <span>{b.paymentMethod}</span>}
          {b.isPaymentPlan && (
            <span>
              Payment plan{b.paymentsLeft ? ` · ${b.paymentsLeft} left` : ''}
              {b.nextPaymentDate ? ` · next ${formatDate(b.nextPaymentDate)}` : ''}
            </span>
          )}
          {Number(b.refunded) > 0 && <span>Refunded {formatCurrency(b.refunded)}</span>}
          {b.couponCode && (
            <span>
              Coupon <span className="tw-font-mono tw-text-neutral-900">{b.couponCode}</span> · −{formatCurrency(b.couponValue)}
            </span>
          )}
          {Number(b.giftCardValue) > 0 && <span>Gift card · −{formatCurrency(b.giftCardValue)}</span>}
        </div>
      </dl>
      {lines.length > 0 && (
        <div className="tw-mt-3">
          <p className="tw-text-xs tw-text-neutral-500">This participant&apos;s charges</p>
          <dl className="tw-m-0 tw-mt-1.5 tw-space-y-1 tw-text-sm">
            {lines.map(([label, value, sign]) => (
              <div key={label} className="tw-flex tw-justify-between tw-gap-3">
                <dt className="tw-font-normal tw-text-neutral-500">{label}</dt>
                <dd className={`tw-m-0 tw-tabular-nums ${sign < 0 ? 'tw-text-green-700' : 'tw-text-neutral-900'}`}>
                  {sign < 0 ? '−' : ''}
                  {formatCurrency(value)}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </section>
  );
}

function MedicalSection({ r }) {
  const { medical, allergy, allergyDeadly } = r.participant;
  const deadly = Number(allergyDeadly) === 1;
  const reported = value => value && !isNone(value);
  if (!reported(medical) && !reported(allergy) && !deadly) {
    return (
      <section>
        <h3 className="tw-m-0 tw-text-sm tw-font-semibold tw-text-neutral-900">Medical & allergies</h3>
        <p className="tw-mt-1.5 tw-text-sm tw-text-neutral-400">Nothing reported</p>
      </section>
    );
  }
  return (
    <section className="tw-flex tw-gap-3 tw-rounded-xl tw-bg-red-50 tw-p-4">
      <HeartPulseIcon className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-text-red-700" aria-hidden />
      <div className="tw-min-w-0 tw-space-y-1.5">
        <h3 className="tw-m-0 tw-text-sm tw-font-semibold tw-text-red-700">Medical & allergies</h3>
        {reported(medical) && (
          <p className="tw-text-sm tw-text-neutral-900">
            <span className="tw-text-neutral-500">Medical: </span>
            {medical}
          </p>
        )}
        {(reported(allergy) || deadly) && (
          <p className="tw-text-sm tw-text-neutral-900">
            <span className="tw-text-neutral-500">Allergies: </span>
            {reported(allergy) ? allergy : 'Not listed'}
          </p>
        )}
        {deadly && (
          <p className="tw-inline-flex tw-items-center tw-gap-1 tw-text-sm tw-font-semibold tw-text-red-700">
            <ShieldAlertIcon className="tw-h-4 tw-w-4" aria-hidden />
            Allergy is life-threatening
          </p>
        )}
      </div>
    </section>
  );
}

function DocumentsSection({ r }) {
  const { items, required, received } = documentChecklist(r);
  const review = medicalReviewLabel(r);
  return (
    <section aria-labelledby="reports-drawer-documents">
      <div className="tw-flex tw-items-baseline tw-justify-between">
        <h3 id="reports-drawer-documents" className="tw-m-0 tw-text-sm tw-font-semibold tw-text-neutral-900">
          Documents
        </h3>
        <span className="tw-text-xs tw-text-neutral-500">
          {received} of {required} received
        </span>
      </div>
      <ul className="tw-mt-2 tw-divide-y tw-divide-neutral-200">
        {items.map(item => (
          <li key={item.label} className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-py-2.5 tw-text-sm">
            <span className="tw-text-neutral-900">{item.label}</span>
            {item.state === 'received' ? (
              <span className="tw-inline-flex tw-items-center tw-gap-1 tw-font-medium tw-text-green-700">
                <CheckIcon className="tw-h-4 tw-w-4" aria-hidden />
                Received
              </span>
            ) : item.state === 'missing' ? (
              <span className="tw-font-medium tw-text-amber-700">Missing</span>
            ) : (
              <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-neutral-400">
                <MinusIcon className="tw-h-4 tw-w-4" aria-hidden />
                Not required{item.note ? ` (${item.note})` : ''}
              </span>
            )}
          </li>
        ))}
        <li className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-py-2.5 tw-text-sm">
          <span className="tw-text-neutral-900">Medical review</span>
          <span
            className={`tw-font-medium ${review === 'Approved' ? 'tw-text-green-700' : review === 'Needs review' ? 'tw-text-amber-700' : 'tw-font-normal tw-text-neutral-400'}`}>
            {review}
          </span>
        </li>
      </ul>
      {r.reminders?.documentsSentAt ? (
        <p className="tw-mt-1 tw-text-xs tw-text-neutral-400">Last reminder sent {formatDate(r.reminders.documentsSentAt)}</p>
      ) : null}
    </section>
  );
}
